import TileGrid from 'ol/tilegrid/TileGrid';
import TileLayer from 'ol/layer/Tile';
import XYZ from 'ol/source/XYZ';
import { getCopyrightLabel } from '../utils';
import { projection, projection3857 } from '../constants';
import ImageLayer from 'ol/layer/Image';
import ImageArcGISRest from 'ol/source/ImageArcGISRest';
import LayerGroup from 'ol/layer/Group';
import { MapboxVectorLayer } from 'ol-mapbox-style';

const geoportalUrl = (type: string) => {
  return `https://www.geoportal.lt/mapproxy/${type}`;
};

const geoportalCopyright = (type: string) => {
  return getCopyrightLabel('geoportal.lt', `${geoportalUrl(type)}/MapServer`);
};

const geoportalTileUrl = (type: string) => {
  return `${geoportalUrl(type)}/MapServer/tile/{z}/{y}/{x}`;
};

const nztCopyright = getCopyrightLabel(
  'Nacionalinė žemės tarnyba prie Aplinkos ministerijos',
  'https://www.nzt.lt/',
);

const rcCopyright = getCopyrightLabel('VĮ Registrų centras', 'https://www.rc.lt/');

const nztUrl = (type: string) => {
  return `${geoportalUrl(type)}/MapServer/tile/{z}/{y}/{x}`;
};

const kpdCopyright = getCopyrightLabel('Kultūros paveldo departamentas', 'https://kvr.kpd.lt');

const crossOrigin = 'Anonymous';

export const vectorBright = {
  id: 'vectorBright',
  title: 'Topografinis (šviesus)',
  layer: new MapboxVectorLayer({
    styleUrl: 'https://basemap.biip.lt/styles/bright/style.json',
  }),
};
export const vectorPositron = {
  id: 'vectorPositron',
  title: 'Pilkas',
  layer: new MapboxVectorLayer({
    styleUrl: 'https://basemap.biip.lt/styles/positron/style.json',
  }),
};

export const geoportalTopo = {
  id: 'geoportalTopo',
  title: 'Topografinis',
  layer: new TileLayer({
    source: new XYZ({
      crossOrigin,
      attributions: geoportalCopyright('gisc_pagrindinis'),
      url: geoportalTileUrl('gisc_pagrindinis'),
      tileGrid: new TileGrid({
        extent: [-3868431.3448, 3787209.7969000004, 3227425.9922, 9284025.053],
        origin: [-5122000, 10000100],
        resolutions: [
          1322.9193125052918, 793.7515875031751, 529.1677250021168, 264.5838625010584,
          132.2919312505292, 66.1459656252646, 26.458386250105836, 13.229193125052918,
          6.614596562526459, 2.6458386250105836, 1.3229193125052918, 0.5291677250021167,
          0.26458386250105836, 0.13229193125052918,
        ],
        tileSize: [512, 512],
      }),
      projection,
    }),
  }),
};

export const geoportalTopo3857 = {
  id: 'geoportalTopo3857',
  title: 'Topografinis',
  layer: new TileLayer({
    source: new XYZ({
      crossOrigin,
      attributions: geoportalCopyright('gisc_pagrindinis_wm'),
      url: geoportalTileUrl('gisc_pagrindinis_wm'),
      tileGrid: new TileGrid({
        extent: [-2337708.2691449076, 3105406.5486537516, 7681044.112130543, 18383409.96838039],
        origin: [-20037508.342787, 20037508.342787],
        resolutions: [
          2445.98490512499, 1222.992452562495, 611.4962262813797, 305.74811314055756,
          152.87405657041106, 76.43702828507324, 38.21851414253662, 19.10925707126831,
          9.554628535634155, 4.77731426794937, 2.388657133974685, 1.1943285668550503,
          0.5971642835598172,
        ],
        tileSize: [256, 256],
      }),
      projection: projection3857,
    }),
  }),
};

export const geoportalOrto = {
  id: 'geoportalOrto',
  title: 'Ortofoto',
  invertColors: true,
  layer: new TileLayer({
    source: new XYZ({
      crossOrigin,
      attributions: nztCopyright,
      url: nztUrl('nzt_ort10lt_recent'),
      tileGrid: new TileGrid({
        extent: [125559.50331997534, 5922338.972788963, 846285.9447728583, 6303868.902515489],
        origin: [-5122000, 10000100],
        resolutions: [
          793.7515875031751, 529.1677250021168, 264.5838625010584, 132.2919312505292,
          66.1459656252646, 26.458386250105836, 13.229193125052918, 6.614596562526459,
          2.6458386250105836, 1.3229193125052918, 0.5291677250021167, 0.26458386250105836,
        ],
        tileSize: [256, 256],
      }),
      projection,
    }),
  }),
};

export const geoportalTopoGray = {
  id: 'geoportalTopoGray',
  title: 'Topografinis, pilkas',
  layer: new TileLayer({
    source: new XYZ({
      crossOrigin,
      attributions: geoportalCopyright('gisc_pilkas'),
      url: geoportalTileUrl('gisc_pilkas'),
      tileGrid: new TileGrid({
        extent: [1845.9797000000253, 5718306.1807, 1017848.0116999997, 6527932.799900001],
        origin: [-5122000, 10000100],
        resolutions: [
          1322.9193125052918, 793.7515875031751, 529.1677250021168, 264.5838625010584,
          132.2919312505292, 66.1459656252646, 26.458386250105836, 13.229193125052918,
          6.614596562526459, 2.6458386250105836, 1.3229193125052918, 0.5291677250021167,
          0.26458386250105836,
        ],
        tileSize: [512, 512],
      }),
      projection,
    }),
  }),
};

export const geoportalOrto1995 = {
  id: 'geoportalOrto1995',
  title: 'ORT10LT 1995-2001',
  layer: new TileLayer({
    source: new XYZ({
      crossOrigin,
      attributions: nztCopyright,
      url: nztUrl('nzt_ort10lt_1995_2001_public'),
      tileGrid: new TileGrid({
        extent: [125559.50331997534, 5922338.972788963, 846285.9447728583, 6303868.902515489],
        origin: [-5122000, 10000100],
        resolutions: [
          793.7515875031751, 529.1677250021168, 264.5838625010584, 132.2919312505292,
          66.1459656252646, 26.458386250105836, 13.229193125052918, 6.614596562526459,
          2.6458386250105836, 1.3229193125052918, 0.5291677250021167, 0.26458386250105836,
        ],
        tileSize: [256, 256],
      }),
      projection,
    }),
  }),
};

export const geoportalOrto2005 = {
  id: 'geoportalOrto2005',
  title: 'ORT10LT 2005-2006',
  layer: new TileLayer({
    source: new XYZ({
      attributions: nztCopyright,
      url: nztUrl('nzt_ort10lt_2005_2006_public'),
      tileGrid: new TileGrid({
        extent: [125559.50331997534, 5922338.972788963, 846285.9447728583, 6303868.902515489],
        origin: [-5122000, 10000100],
        resolutions: [
          793.7515875031751, 529.1677250021168, 264.5838625010584, 132.2919312505292,
          66.1459656252646, 26.458386250105836, 13.229193125052918, 6.614596562526459,
          2.6458386250105836, 1.3229193125052918, 0.5291677250021167, 0.26458386250105836,
        ],
        tileSize: [256, 256],
      }),
      projection,
    }),
  }),
};

export const geoportalOrto2009 = {
  id: 'geoportalOrto2009',
  title: 'ORT10LT 2009-2010',
  layer: new TileLayer({
    source: new XYZ({
      crossOrigin,
      attributions: nztCopyright,
      url: nztUrl('nzt_ort10lt_2009_2010_public'),
      tileGrid: new TileGrid({
        extent: [125559.50331997534, 5922338.972788963, 846285.9447728583, 6303868.902515489],
        origin: [-5122000, 10000100],
        resolutions: [
          793.7515875031751, 529.1677250021168, 264.5838625010584, 132.2919312505292,
          66.1459656252646, 26.458386250105836, 13.229193125052918, 6.614596562526459,
          2.6458386250105836, 1.3229193125052918, 0.5291677250021167, 0.26458386250105836,
        ],
        tileSize: [256, 256],
      }),
      projection,
    }),
  }),
};

export const geoportalOrto2012 = {
  id: 'geoportalOrto2012',
  title: 'ORT10LT 2012-2013',
  layer: new TileLayer({
    source: new XYZ({
      attributions: nztCopyright,
      url: nztUrl('nzt_ort10lt_2012_2013_public'),
      tileGrid: new TileGrid({
        extent: [125559.50331997534, 5922338.972788963, 846285.9447728583, 6303868.902515489],
        origin: [-5122000, 10000100],
        resolutions: [
          793.7515875031751, 529.1677250021168, 264.5838625010584, 132.2919312505292,
          66.1459656252646, 26.458386250105836, 13.229193125052918, 6.614596562526459,
          2.6458386250105836, 1.3229193125052918, 0.5291677250021167, 0.26458386250105836,
        ],
        tileSize: [256, 256],
      }),
      projection,
    }),
  }),
};

export const geoportalOrto2015 = {
  id: 'geoportalOrto2015',
  title: 'ORT10LT 2015-2017',
  layer: new TileLayer({
    source: new XYZ({
      attributions: nztCopyright,
      url: nztUrl('nzt_ort10lt_2015_2017_public'),
      tileGrid: new TileGrid({
        extent: [125559.50331997534, 5922338.972788963, 846285.9447728583, 6303868.902515489],
        origin: [-5122000, 10000100],
        resolutions: [
          793.7515875031751, 529.1677250021168, 264.5838625010584, 132.2919312505292,
          66.1459656252646, 26.458386250105836, 13.229193125052918, 6.614596562526459,
          2.6458386250105836, 1.3229193125052918, 0.5291677250021167, 0.26458386250105836,
          0.13229193125052918,
        ],
        tileSize: [256, 256],
      }),
      projection,
    }),
  }),
};

export const geoportalOrto2018 = {
  id: 'geoportalOrto2018',
  title: 'ORT10LT 2018-2020',
  layer: new TileLayer({
    source: new XYZ({
      crossOrigin,
      attributions: nztCopyright,
      url: nztUrl('nzt_ort10lt_2018_2020_public'),
      tileGrid: new TileGrid({
        extent: [125559.50331997534, 5922338.972788963, 846285.9447728583, 6303868.902515489],
        origin: [-5122000, 10000100],
        resolutions: [
          793.7515875031751, 529.1677250021168, 264.5838625010584, 132.2919312505292,
          66.1459656252646, 26.458386250105836, 13.229193125052918, 6.614596562526459,
          2.6458386250105836, 1.3229193125052918, 0.5291677250021167, 0.26458386250105836,
          0.13229193125052918,
        ],
        tileSize: [256, 256],
      }),
      projection,
    }),
  }),
};

export const geoportalOrto2021 = {
  id: 'geoportalOrto2021',
  title: 'ORT10LT 2021-2023',
  layer: new TileLayer({
    source: new XYZ({
      crossOrigin,
      attributions: nztCopyright,
      url: nztUrl('nzt_ort10lt_2021_2023'),
      tileGrid: new TileGrid({
        extent: [125559.50331997534, 5922338.972788963, 846285.9447728583, 6303868.902515489],
        origin: [-5122000, 10000100],
        resolutions: [
          793.7515875031751, 529.1677250021168, 264.5838625010584, 132.2919312505292,
          66.1459656252646, 26.458386250105836, 13.229193125052918, 6.614596562526459,
          2.6458386250105836, 1.3229193125052918, 0.5291677250021167, 0.26458386250105836,
          0.13229193125052918,
        ],
        tileSize: [256, 256],
      }),
      projection,
    }),
  }),
};

export const geoportalOrto2024 = {
  id: 'geoportalOrto2024',
  title: 'ORT10LT 2024-2026',
  layer: new TileLayer({
    source: new XYZ({
      crossOrigin,
      attributions: nztCopyright,
      url: nztUrl('nzt_ort10lt_2024_2026'),
      tileGrid: new TileGrid({
        extent: [125559.50331997534, 5922338.972788963, 846285.9447728583, 6303868.902515489],
        origin: [-5122000, 10000100],
        resolutions: [
          793.7515875031751, 529.1677250021168, 264.5838625010584, 132.2919312505292,
          66.1459656252646, 26.458386250105836, 13.229193125052918, 6.614596562526459,
          2.6458386250105836, 1.3229193125052918, 0.5291677250021167, 0.26458386250105836,
          0.13229193125052918,
        ],
        tileSize: [256, 256],
      }),
      projection,
    }),
  }),
};

export const geoportalOrtoGroup = {
  id: 'geoportalOrtoGroup',
  title: 'Ortofotografiniai žemėlapiai',
  description: nztCopyright,
  sublayers: [
    {
      name: 'ORT10LT 1995-2001',
      layer: geoportalOrto1995.layer,
      id: geoportalOrto1995.id,
    },
    {
      name: 'ORT10LT 2005-2006',
      layer: geoportalOrto2005.layer,
      id: geoportalOrto2005.id,
    },
    {
      name: 'ORT10LT 2009-2010',
      layer: geoportalOrto2009.layer,
      id: geoportalOrto2009.id,
    },
    {
      name: 'ORT10LT 2012-2013',
      layer: geoportalOrto2012.layer,
      id: geoportalOrto2012.id,
    },
    {
      name: 'ORT10LT 2015-2017',
      layer: geoportalOrto2015.layer,
      id: geoportalOrto2015.id,
    },
    {
      name: 'ORT10LT 2018-2020',
      layer: geoportalOrto2018.layer,
      id: geoportalOrto2018.id,
    },
    {
      name: 'ORT10LT 2021-2023',
      layer: geoportalOrto2021.layer,
      id: geoportalOrto2021.id,
    },
    {
      name: 'ORT10LT 2024-2026',
      layer: geoportalOrto2024.layer,
      id: geoportalOrto2024.id,
    },
  ],
  layer: new LayerGroup({
    layers: [
      geoportalOrto1995.layer,
      geoportalOrto2005.layer,
      geoportalOrto2009.layer,
      geoportalOrto2012.layer,
      geoportalOrto2015.layer,
      geoportalOrto2018.layer,
      geoportalOrto2021.layer,
      geoportalOrto2024.layer,
    ],
  }),
};

export const geoportalHybrid = {
  id: 'geoportalHybrid',
  title: 'Mišrus',
  layer: new TileLayer({
    source: new XYZ({
      attributions: geoportalCopyright('gisc_misrus_public'),
      url: geoportalTileUrl('gisc_misrus_public'),
      tileGrid: new TileGrid({
        extent: [236725.44661756046, 5930813.423926848, 717678.0543561094, 6291634.374735416],
        origin: [-5122000, 10000100],
        resolutions: [
          1322.9193125052918, 793.7515875031751, 529.1677250021168, 264.5838625010584,
          132.2919312505292, 66.1459656252646, 26.458386250105836, 13.229193125052918,
          6.614596562526459, 2.6458386250105836,
        ],
        tileSize: [512, 512],
      }),
      projection,
    }),
  }),
};

export const geoportalGrpk = {
  id: 'geoportalGrpk',
  title: 'GRPK',
  layer: new TileLayer({
    source: new XYZ({
      crossOrigin,
      attributions: geoportalCopyright('gisc_misrus_pugisc_grpkblic'),
      url: geoportalTileUrl('gisc_grpk'),
      tileGrid: new TileGrid({
        extent: [125559.50331997534, 5922338.972788963, 846285.9447728583, 6303868.902515489],
        origin: [-5122000, 10000100],
        resolutions: [
          1322.9193125052918, 793.7515875031751, 529.1677250021168, 264.5838625010584,
          132.2919312505292, 66.1459656252646, 26.458386250105836, 13.229193125052918,
          6.614596562526459, 2.6458386250105836, 1.3229193125052918, 0.5291677250021167,
          0.26458386250105836,
        ],
        tileSize: [256, 256],
      }),
      projection,
    }),
  }),
};

export const geoportalForests = {
  id: 'geoportalForests',
  title: 'Miškai',
  layer: new ImageLayer({
    source: new ImageArcGISRest({
      attributions: geoportalCopyright('vmt_mkd'),
      ratio: 1,
      params: {
        LAYERS: 'show:0,1,2,4,5,9',
      },
      url: `${geoportalUrl('vmt_mkd')}/MapServer`,
    }),
  }),
  sublayers: [
    {
      name: 'VMU urėdijos regioniniai padaliniai',
      value: '0',
    },
    {
      name: 'Urėdijos',
      value: '1',
    },
    {
      name: 'Girininkijos',
      value: '2',
    },
    {
      name: 'Girininkijos iki 2022-12-31',
      value: '3',
    },
    {
      name: 'Kertinės miško buveinės',
      value: '4',
    },
    {
      name: 'Kvartalai',
      value: '5',
    },
    {
      name: 'Kvartalai iki 2022-12-31',
      value: '6',
    },
    {
      name: 'Valstybinės reikšmės miškų plotų ribos',
      value: '7',
    },
    {
      name: 'Miškų pogrupiai',
      value: '8',
    },
    {
      name: 'Miško sklypai',
      value: '9',
    },
  ],
};

geoportalForests.layer.set('type', 'ARCGIS');

// Gamtotvarkos žemėlapiui miškai suskaidyti į DU atskirus geoportal servisus
// (užsakovo prašymas, gamtotvarka-public#39 punktas 8). Anksčiau čia buvo viena
// „Miškai" kopija, rodanti į `vmt_mkd` su sluoksnių id 0,1,2,4,5,9 — tie id
// nebeegzistuoja: geoportal servisą suskaidė, dalis sluoksnių persikėlė į
// `vmt_valst_reiksmes_miskai`, o `vmt_mkd` liko tik Kvartalai ir Miško sklypai.
// Bendras `geoportalForests` (alis/rusys/medziokle) sąmoningai NEliečiamas.
export const gamtotvarkaForestResources = {
  id: 'gamtotvarkaForestResources',
  title: 'Miškų išteklių informacijos duomenys',
  layer: new ImageLayer({
    source: new ImageArcGISRest({
      attributions: geoportalCopyright('vmt_valst_reiksmes_miskai'),
      ratio: 1,
      params: {
        LAYERS: 'show:0,1,2,3,4,5,6',
      },
      url: `${geoportalUrl('vmt_valst_reiksmes_miskai')}/MapServer`,
    }),
  }),
  sublayers: [
    {
      name: 'VMT Miškininkystės departamento skyrių teritorijos',
      value: '0',
    },
    {
      name: 'VMU regioninių padalinių ribos',
      value: '1',
    },
    {
      name: 'Girininkijų ribos',
      value: '2',
    },
    {
      name: 'Savaiminukų inventorizacija',
      value: '3',
    },
    {
      name: 'Žemė apauganti mišku',
      value: '4',
    },
    {
      name: 'Valstybinės reikšmės miškų plotų ribos',
      value: '5',
    },
    {
      name: 'Miškų grupės ir pogrupiai',
      value: '6',
    },
  ],
};

gamtotvarkaForestResources.layer.set('id', 'gamtotvarkaForestResources');
gamtotvarkaForestResources.layer.set('type', 'ARCGIS');

export const gamtotvarkaForestCadastre = {
  id: 'gamtotvarkaForestCadastre',
  title: 'Miškų valstybės kadastro duomenys',
  layer: new ImageLayer({
    source: new ImageArcGISRest({
      attributions: geoportalCopyright('vmt_mkd'),
      ratio: 1,
      params: {
        LAYERS: 'show:0,1',
      },
      url: `${geoportalUrl('vmt_mkd')}/MapServer`,
    }),
  }),
  sublayers: [
    {
      name: 'Kvartalai',
      value: '0',
    },
    {
      name: 'Miško sklypai',
      value: '1',
    },
  ],
};

gamtotvarkaForestCadastre.layer.set('id', 'gamtotvarkaForestCadastre');
gamtotvarkaForestCadastre.layer.set('type', 'ARCGIS');

export const rcSzns = {
  id: 'rcSzns',
  title: 'Specialiųjų žemės naudojimo sąlygų (SŽNS) duomenų rinkinys',
  layer: new TileLayer({
    source: new XYZ({
      crossOrigin,
      attributions: rcCopyright,
      url: geoportalTileUrl('rc_szns'),
      tileGrid: new TileGrid({
        extent: [189017.85510000214, 5973241.050000006, 680153.2719000028, 6257863.444000006],
        origin: [18900, 6258000],
        resolutions: [
          1587.5031750063501, 793.7515875031751, 529.1677250021168, 264.5838625010584,
          132.2919312505292, 52.91677250021167, 26.458386250105836, 13.229193125052918,
          6.614596562526459, 2.6458386250105836, 1.3229193125052918, 0.5291677250021167,
          0.26458386250105836,
        ],
        tileSize: [512, 512],
      }),
      projection,
    }),
  }),
};

rcSzns.layer.set('type', 'ARCGIS');

export const geoportalKvr = {
  id: 'geoportalKvr',
  title: 'Kultūros vertybių registras',
  description: kpdCopyright,
  layer: new ImageLayer({
    source: new ImageArcGISRest({
      attributions: geoportalCopyright('kpd_kvr'),
      ratio: 1,
      params: {
        LAYERS: 'show:0,1,2',
      },
      url: `${geoportalUrl('kpd_kvr')}/MapServer`,
    }),
  }),
  sublayers: [
    {
      name: 'Kultūros vertybių apsaugos zonos',
      value: '2',
    },
    {
      name: 'Plotiniai objektai',
      value: '1',
    },
    {
      name: 'Taškiniai objektai',
      value: '0',
    },
  ],
};

geoportalKvr.layer.set('type', 'ARCGIS');

export const sentinelPlyniKirtimai = {
  id: 'sentinelPlyniKirtimai',
  title: 'Sentinel 2 plyni kirtimai',
  description: getCopyrightLabel('Valstybinė miškų tarnyba', 'https://amvmt.lrv.lt'),
  layer: new ImageLayer({
    source: new ImageArcGISRest({
      attributions: getCopyrightLabel('Valstybinė miškų tarnyba', 'https://amvmt.lrv.lt'),
      ratio: 1,
      params: {
        LAYERS: 'show:0,1,2',
      },
      url: `https://kadastras.amvmt.lt/arcgis/rest/services/Webmaps/sentinel_plyni_kirtimai/MapServer`,
    }),
  }),
};

sentinelPlyniKirtimai.layer.set('type', 'ARCGIS');
